import { motion } from "framer-motion";
import { Globe2, Compass, Handshake } from "lucide-react";
import { TRADE_IMG } from "@/constants/site";

const PILLARS = [
  {
    icon: Globe2,
    title: "Borderless Perspective",
    text: "We see markets the way global buyers and partners see them — across cultures, regulations, and time zones.",
  },
  {
    icon: Compass,
    title: "Practical Navigation",
    text: "From compliance to logistics, we translate the complexity of international expansion into clear next steps.",
  },
  {
    icon: Handshake,
    title: "Trusted Connections",
    text: "We open doors to vetted buyers, suppliers, and partners so every introduction has real commercial intent.",
  },
];

export const About = () => (
  <section id="about" data-testid="about-section" className="relative overflow-hidden bg-royal-deep py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold sm:text-xs">About Rayasthaila</p>
        <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
          A bridge between ambition and opportunity.
        </h2>
        <p className="mt-8 text-base leading-relaxed text-slate-300 sm:text-lg">
          Rayasthaila is an international business and trade consultancy focused on connecting businesses across
          borders. We help companies explore new markets, develop international partnerships, identify business and
          trade opportunities, and navigate the practical requirements of entering or expanding into global markets.
        </p>
        <p className="mt-5 text-base leading-relaxed text-slate-400">
          Our work spans international trade consulting, market-entry strategy, buyer and supplier sourcing, business
          partnerships, trade representation, and import-export advisory — turning cross-border opportunities into
          practical growth.
        </p>

        <div className="mt-12 space-y-8">
          {PILLARS.map(({ icon: Icon, title, text }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex gap-5"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
                <Icon size={18} />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold text-white">{title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <div className="absolute -inset-4 rounded-2xl border border-gold/25" />
        <div className="relative overflow-hidden rounded-xl">
          <img
            src={TRADE_IMG}
            alt="Global cargo ship at port during sunset"
            className="aspect-[4/5] w-full object-cover"
            data-testid="about-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-royal-deep/80 via-transparent to-transparent" />
        </div>
        <div className="absolute -bottom-8 -left-4 rounded-xl border border-gold/25 bg-royal-card/90 px-7 py-5 shadow-2xl shadow-black/50 backdrop-blur-xl sm:-left-8">
          <p className="font-mono text-2xl font-semibold text-gold">Global</p>
          <p className="mt-1 max-w-[180px] text-xs leading-snug text-slate-300">
            Trade networks across healthcare, technology, manufacturing & beyond
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);
